import { castArray, concat, flattenDeep, isNil } from 'lodash';
import moment from 'moment/moment';
import { invalidateProjectsList, PROJECTS_TAG } from '../../../data/actions';

export const SET_ACTIVE_PROJECT_SECTION = 'SET_ACTIVE_PROJECT_SECTION';
export const SET_ACTIVE_PROJECT = 'SET_ACTIVE_PROJECT';

export const SINGLE_PROJECT_BASIC_SECTION = 'basic';
export const SINGLE_PROJECT_EXPERIMENTAL_SECTION = 'experimental';
export const SINGLE_PROJECT_BOOKING_SECTION = 'booking';
export const SINGLE_PROJECT_DATA_EXTRACTION_SECTION = 'file-import';
export const SINGLE_PROJECT_POST_PROCESSING_SECTION = 'post-processing';

export const SINGLE_PROJECT_HISTORY_SECTION = 'history';
export const SINGLE_PROJECT_DOCUMENTS_SECTION = 'document';
const SINGLE_PROJECT_SECTIONS = [
  SINGLE_PROJECT_BASIC_SECTION,
  SINGLE_PROJECT_EXPERIMENTAL_SECTION,
  SINGLE_PROJECT_BOOKING_SECTION,
  SINGLE_PROJECT_DATA_EXTRACTION_SECTION,
  SINGLE_PROJECT_POST_PROCESSING_SECTION,
  SINGLE_PROJECT_HISTORY_SECTION,
  SINGLE_PROJECT_DOCUMENTS_SECTION
];

export const SET_ACTIVE_PROJECT_CONDITIONS = 'SET_ACTIVE_PROJECT_CONDITIONS';

/**
 * Project Sub Resources
 */
/**
 * Sets the conditions for a single project
 * @param conditions
 * @returns {{type: string, conditions: *}}
 */
export const setProjectConditions = conditions => {
  return {
    type: SET_ACTIVE_PROJECT_CONDITIONS,
    conditions
  };
};

/**
 * get conditions of a  single active project
 * @param projectId
 * @returns true
 */
export const getProjectConditions = projectId => {
  return async (dispatch, getState, { apiClient }) => {
    try {
      const sanitizedProjectId = projectId || getState().projects.single.active.id;
      const conditions = await apiClient.call(PROJECTS_TAG, 'getProjectConditions', {
        projectId: sanitizedProjectId
      });
      conditions.data.forEach(condition => {
        const { occurrences } = condition;
        condition.occurrences = [[], []]; // eslint-disable-line no-param-reassign

        for (let i = 0; i < 50; i++) {
          condition.occurrences[0].push(null);
          condition.occurrences[1].push(null);
        }

        occurrences.forEach((occurrence, index) => {
          /**
           * Missing part of webservice to return name and type
           */

          if (!isNil(condition.occurrences[occurrence.x_location][occurrence.y_location])) {
            // eslint-disable-next-line no-param-reassign
            condition.occurrences[occurrence.x_location][occurrence.y_location] = flattenDeep(
              concat(
                castArray(condition.occurrences[occurrence.x_location][occurrence.y_location]),
                occurrence
              )
            );
          } else {
            condition.occurrences[occurrence.x_location][occurrence.y_location] = occurrence; // eslint-disable-line no-param-reassign
          }
        });
      });

      dispatch(setProjectConditions(conditions.data));
    } catch (e) {
      console.log('getProjectConditions error'); // eslint-disable-line no-console
      console.log(e); // eslint-disable-line no-console
    }
  };
};

/**
 * Sets the active section for a single active project
 * @param section
 * @returns {{type: string, section: *}}
 */
export const setActiveProjectSection = section => {
  if (SINGLE_PROJECT_SECTIONS.indexOf(section) >= 0) {
    return {
      type: SET_ACTIVE_PROJECT_SECTION,
      section
    };
  }
  return null;
};

/**
 * Set the active project
 * @param active
 * @returns {{type: string, active: *}}
 */
export const setActiveProject = active => {
  return {
    type: SET_ACTIVE_PROJECT,
    active
  };
};

/**
 * Invalidates the active project
 * @returns {Function}
 */
export const invalidateActiveProject = () => {
  return (dispatch, getState, { apiClient }) => {
    dispatch(setActiveProject(null));
  };
};

/**
 * Gets a single project and populates it with it's signals, events and stimuli. Dispatches to the setActiveProject action.
 * @param id
 * @returns {Function}
 */
export const getActiveProject = id => {
  return async (dispatch, getState, { apiClient }) => {
    try {
      const project = await apiClient.call(PROJECTS_TAG, 'getProject', { id });

      dispatch(setActiveProject(project));
      dispatch(getProjectConditions(project.id));
    } catch (e) {
      console.log('getActiveProject error'); // eslint-disable-line no-console
      console.log(e); // eslint-disable-line no-console
    }
  };
};

/**
 * Creates a new project, executes callback on update completion.
 * @param project
 * @param callback
 * @returns {Function}
 */
export const createProject = (project, callback) => {
  return async (dispatch, getState, { apiClient }) => {
    try {
      const projectId = await apiClient.call(PROJECTS_TAG, 'createProject', {}, project);
      dispatch(invalidateProjectsList());
      callback && callback(projectId.id);
    } catch (e) {
      console.log('createProject error'); // eslint-disable-line no-console
      console.log(e); // eslint-disable-line no-console
    }
  };
};

/**
 * Updates a single project, executes callback on update completion.
 * @param project
 * @param callback
 * @returns {Function}
 */
export const updateActiveProject = (project, callback) => {
  return async (dispatch, getState, { apiClient }) => {
    try {
      project.start_date = moment.utc(project.start_date).format('YYYY-MM-DD'); // eslint-disable-line no-param-reassign
      project.end_date = moment.utc(project.end_date).format('YYYY-MM-DD'); // eslint-disable-line no-param-reassign
      await apiClient.call(
        PROJECTS_TAG,
        'updateProject',
        {
          id: project.id
        },
        project
      );
      // Clear list of projects
      dispatch(invalidateProjectsList());
      callback && callback();
    } catch (e) {
      console.log('updateActiveProject error'); // eslint-disable-line no-console
      console.log(e); // eslint-disable-line no-console
    }
  };
};

export const setActiveProjectLocking = (project, locked = true, callback) => {
  return async (dispatch, getState, { apiClient }) => {
    try {
      const updatedProject = await apiClient.call(
        PROJECTS_TAG,
        'patchProject',
        {
          id: project.id
        },
        {
          template_locked: locked
        }
      );
      // Clear list of projects
      dispatch(setActiveProject(updatedProject));
      dispatch(invalidateProjectsList());
      callback && callback();
    } catch (e) {
      console.log('setActiveProjectLocking error'); // eslint-disable-line no-console
      console.log(e); // eslint-disable-line no-console
    }
  };
};

/**
 * Deletes a single project, executes callback on update completion.
 * @param id
 * @param callback
 * @returns {Function}
 */
export const deleteProject = (id, callback) => {
  return async (dispatch, getState, { apiClient }) => {
    try {
      await apiClient.call(PROJECTS_TAG, 'deleteProject', { id });
      // Clear list of projects
      dispatch(invalidateProjectsList());
      callback && callback();
    } catch (e) {
      console.log('deleteProject error'); // eslint-disable-line no-console
      console.log(e); // eslint-disable-line no-console
    }
  };
};



// WEBPACK FOOTER //
// ./src/scenes/projects/scenes/singleProject/data/projectActions.js